import { MapLayerSettings, UnitFormat } from '@app/api/mapping/types';
import type { PickingInfo } from '@deck.gl/core';
import { VariableImageData } from './caching';

interface TooltipLayer {
  id: string;
  title: string;
  unitFormat?: UnitFormat;
  variable: string;
  kind: MapLayerSettings['kind'];
  data: VariableImageData;
}

interface MultiLayerTooltipConfig {
  layers: TooltipLayer[];
  followCursor?: boolean;
  followCursorOffset?: number;
  followCursorPlacement?: 'BOTTOM' | 'TOP' | 'RIGHT' | 'LEFT';
}

function wrapLongitude(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

function clampLatitude(lat: number): number {
  const MAX_LAT = 85.051129;
  return Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));
}

export class MultiLayerTooltipControl {
  private _config: MultiLayerTooltipConfig;
  private _parentElement: HTMLElement | null = null;
  private _tooltipElement: HTMLElement | null = null;
  private _isVisible: boolean = false;
  private _lastCursorPosition: { x: number; y: number } = { x: 0, y: 0 };
  private _didSetParentPosition: boolean = false;

  constructor(config: MultiLayerTooltipConfig = { layers: [] }) {
    this._config = config;
  }

  addTo(parentElement: HTMLElement): void {
    this._parentElement = parentElement;
    const currentPosition = window.getComputedStyle(parentElement).position;
    if (currentPosition === 'static') {
      parentElement.style.position = 'relative';
      this._didSetParentPosition = true;
    } else {
      this._didSetParentPosition = false;
    }
    this._createTooltipElement();
  }

  remove(): void {
    if (this._tooltipElement && this._parentElement) {
      this._parentElement.removeChild(this._tooltipElement);
      this._tooltipElement = null;
    }
    if (this._parentElement && this._didSetParentPosition) {
      this._parentElement.style.position = '';
      this._didSetParentPosition = false;
    }
    this._parentElement = null;
    this._isVisible = false;
  }

  updateConfig(config: Partial<MultiLayerTooltipConfig>): void {
    this._config = { ...this._config, ...config };
  }

  getConfig(): MultiLayerTooltipConfig {
    return { ...this._config };
  }

  updatePickingInfo(pickingInfo: PickingInfo): void {
    if (!this._tooltipElement) return;

    const anyInfo = pickingInfo as any;
    const srcEvent = anyInfo?.srcEvent as MouseEvent | PointerEvent | TouchEvent | undefined;

    let viewportX: number | undefined = undefined;
    let viewportY: number | undefined = undefined;

    if (srcEvent && 'clientX' in srcEvent && 'clientY' in srcEvent) {
      viewportX = (srcEvent as MouseEvent).clientX;
      viewportY = (srcEvent as MouseEvent).clientY;
    } else if (
      anyInfo &&
      typeof anyInfo.offsetCenter?.x === 'number' &&
      typeof anyInfo.offsetCenter?.y === 'number'
    ) {
      viewportX = anyInfo.offsetCenter.x;
      viewportY = anyInfo.offsetCenter.y;
    } else if (typeof pickingInfo.x === 'number' && typeof pickingInfo.y === 'number') {
      const canvas: HTMLCanvasElement | null = this._parentElement?.querySelector('canvas') ?? null;
      const canvasRect = canvas?.getBoundingClientRect();
      if (canvasRect) {
        viewportX = pickingInfo.x + canvasRect.left;
        viewportY = pickingInfo.y + canvasRect.top;
      } else {
        viewportX = pickingInfo.x;
        viewportY = pickingInfo.y;
      }
    }

    const canvas: HTMLCanvasElement | null = this._parentElement?.querySelector('canvas') ?? null;
    const canvasRect = canvas?.getBoundingClientRect();
    if (
      canvasRect &&
      typeof viewportX === 'number' &&
      typeof viewportY === 'number' &&
      (viewportX < canvasRect.left ||
        viewportX > canvasRect.right ||
        viewportY < canvasRect.top ||
        viewportY > canvasRect.bottom)
    ) {
      this._hideTooltip();
      return;
    }

    if (typeof viewportX === 'number' && typeof viewportY === 'number') {
      this._lastCursorPosition = { x: viewportX, y: viewportY };
    }

    const [lonRaw, latRaw] = pickingInfo.coordinate ?? [0, 0];

    let tooltipData: Array<{ layer: TooltipLayer; value: number; direction?: number }> = [];
    if (Number.isFinite(lonRaw) && Number.isFinite(latRaw)) {
      const lon = wrapLongitude(lonRaw);
      const lat = clampLatitude(latRaw);
      tooltipData = this._extractTooltipDataFromLonLat(lon, lat);
    } else {
      tooltipData = [];
    }

    if (tooltipData.length > 0) {
      this._showTooltip(tooltipData);
      this._updateTooltipPosition();
    } else {
      this._hideTooltip();
    }
  }

  private _createTooltipElement(): void {
    if (!this._parentElement) return;

    this._tooltipElement = document.createElement('div');
    this._tooltipElement.className = 'multi-layer-tooltip';
    this._tooltipElement.style.cssText = `
      position: absolute;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      pointer-events: none;
      z-index: 1000;
      opacity: 0;
      transition: opacity 0.2s ease;
      max-width: 250px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      line-height: 1.4;
    `;

    this._parentElement.appendChild(this._tooltipElement);
  }

  private _extractTooltipDataFromLonLat(
    lon: number,
    lat: number,
  ): Array<{ layer: TooltipLayer; value: number; direction?: number }> {
    const tooltipData: Array<{ layer: TooltipLayer; value: number; direction?: number }> = [];

    for (const layer of this._config.layers) {
      const { texture, imageUnscale, imageType, bounds } = layer.data;
      const texW = texture.width;
      const texH = texture.height;
      if (!texW || !texH) continue;

      // Use actual texture bounds instead of assuming global coverage
      // bounds format: [minLon, minLat, maxLon, maxLat]
      const [minLon, minLat, maxLon, maxLat] = bounds;

      // Calculate normalized texture coordinates based on actual bounds
      const u = (lon - minLon) / (maxLon - minLon);
      const v = (maxLat - lat) / (maxLat - minLat);

      const px = Math.max(0, Math.min(texW - 1, Math.floor(u * texW)));
      const py = Math.max(0, Math.min(texH - 1, Math.floor(v * texH)));
      const linearIndex = py * texW + px;

      const arr = (texture as any).data as Uint8Array | Uint8ClampedArray | Float32Array;
      if (!ArrayBuffer.isView(arr)) continue;

      const channels = Math.max(1, Math.floor((arr.length as number) / (texW * texH)));

      if (imageType === 'VECTOR') {
        if (channels >= 2) {
          const [minScale, maxScale] = imageUnscale;
          const base = linearIndex * channels;
          const xt = Math.max(0, Math.min(1, arr[base + 0] / 255.0));
          const yt = Math.max(0, Math.min(1, arr[base + 1] / 255.0));
          const x = minScale + xt * (maxScale - minScale);
          const y = minScale + yt * (maxScale - minScale);
          const magnitude = Math.sqrt(x * x + y * y);
          const direction = Math.atan2(x, y);
          if (Number.isFinite(magnitude)) {
            tooltipData.push({ layer, value: magnitude, direction });
          }
          continue;
        }
        continue;
      }

      // SCALAR: use first channel
      const base = linearIndex * channels;
      const scaledValue: number = (arr as any)[base] as number;
      let value: number;
      if (arr instanceof Float32Array) {
        value = scaledValue;
      } else {
        const [minScale, maxScale] = imageUnscale;
        const t = Math.max(0, Math.min(1, scaledValue / 255.0));
        value = minScale + t * (maxScale - minScale);
      }

      if (Number.isFinite(value)) {
        tooltipData.push({ layer, value });
      }
    }

    return tooltipData;
  }

  private _showTooltip(
    tooltipData: Array<{ layer: TooltipLayer; value: number; direction?: number }>,
  ): void {
    if (!this._tooltipElement) return;
    const content = this._formatTooltipContent(tooltipData);
    this._tooltipElement.innerHTML = content;
    this._tooltipElement.style.opacity = '1';
    this._isVisible = true;
  }

  private _hideTooltip(): void {
    if (!this._tooltipElement) return;
    this._tooltipElement.style.opacity = '0';
    this._isVisible = false;
  }

  private _formatTooltipContent(
    tooltipData: Array<{ layer: TooltipLayer; value: number; direction?: number }>,
  ): string {
    return tooltipData
      .map(({ layer, value, direction }) => {
        const formattedValue = this._formatValue(value, layer.unitFormat);
        const directionText =
          direction !== undefined ? ` (${this._formatDirection(direction)})` : '';
        return `
        <div style="margin-bottom: 4px;">
          <div style="font-weight: 600; font-size: 11px; color: #ccc; margin-bottom: 1px;">
            ${layer.title}
          </div>
          <div style="font-size: 13px;">
            ${formattedValue}${directionText}
          </div>
        </div>`;
      })
      .join('');
  }

  private _formatValue(value: number, unitFormat?: UnitFormat): string {
    if (!unitFormat) return value.toFixed(2);
    let adjustedValue = value;
    if (unitFormat.offset !== undefined) adjustedValue += unitFormat.offset;
    if (unitFormat.scale !== undefined) adjustedValue *= unitFormat.scale;
    const decimals = unitFormat.decimals ?? 1;
    return `${adjustedValue.toFixed(decimals)} ${unitFormat.unit}`;
  }

  private _formatDirection(direction: number): string {
    const degrees = ((direction * 180) / Math.PI + 360) % 360;
    const cardinals = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return cardinals[index];
  }

  private _updateTooltipPosition(): void {
    if (!this._tooltipElement || !this._isVisible || !this._parentElement) return;

    const offset = this._config.followCursorOffset ?? 10;
    const placement = this._config.followCursorPlacement ?? 'BOTTOM';

    const parentRect = this._parentElement.getBoundingClientRect();
    let x = this._lastCursorPosition.x - parentRect.left;
    let y = this._lastCursorPosition.y - parentRect.top;

    const tooltipRect = this._tooltipElement.getBoundingClientRect();

    switch (placement) {
      case 'BOTTOM':
        y += offset;
        x -= tooltipRect.width / 2;
        break;
      case 'TOP':
        y -= tooltipRect.height + offset;
        x -= tooltipRect.width / 2;
        break;
      case 'RIGHT':
        x += offset;
        y -= tooltipRect.height / 2;
        break;
      case 'LEFT':
        x -= tooltipRect.width + offset;
        y -= tooltipRect.height / 2;
        break;
    }

    const parentWidth = parentRect.width;
    const parentHeight = parentRect.height;

    if (x + tooltipRect.width > parentWidth) x = parentWidth - tooltipRect.width - 5;
    if (x < 0) x = 5;
    if (y + tooltipRect.height > parentHeight)
      y = this._lastCursorPosition.y - parentRect.top - tooltipRect.height - offset;
    if (y < 0) y = this._lastCursorPosition.y - parentRect.top + offset;

    x = Math.max(5, Math.min(x, parentWidth - tooltipRect.width - 5));
    y = Math.max(5, Math.min(y, parentHeight - tooltipRect.height - 5));

    this._tooltipElement.style.left = `${x}px`;
    this._tooltipElement.style.top = `${y}px`;
  }
}
